import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const ADMIN_EMAIL = "admin@g-repair.de";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(ADMIN_EMAIL);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Willkommen zurück.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falsches Passwort");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] grain flex items-center justify-center px-6">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,255,255,0.06),transparent_55%)]" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md glass rounded-3xl p-10"
      >
        <a href="/" className="font-display text-2xl text-white flex items-center gap-2 mb-8">
          <span className="inline-block w-2 h-2 rounded-full bg-white" />
          G-Repair
        </a>
        <div className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center text-white mb-5">
          <Lock size={20} />
        </div>
        <h1 className="font-display text-4xl text-white">Admin-Bereich</h1>
        <p className="text-[#999] text-sm mt-2 mb-8">Melde dich mit deiner E-Mail und deinem Passwort an, um Bestellungen, Preise und Rechnungen zu verwalten.</p>

        <form onSubmit={submit} data-testid="login-form" className="space-y-5">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">E-Mail</label>
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="deine@email.de"
              className="w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Passwort</label>
            <input data-testid="login-password" type="password" required autoFocus value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
              className="w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300" />
          </div>
          <button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full rounded-full bg-white text-[#050505] py-4 font-medium hover:bg-[#d1d5db] transition-colors duration-300 disabled:opacity-50">
            {loading ? "Anmelden…" : "Anmelden"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
